/**
 * SubdivisionR — country-dependent state/province field.
 *
 * Used for standalone subdivision fields (e.g. applicant.place_of_birth.state)
 * that pair with a sibling country field named by `field.subdivisions_of`.
 *
 * Behaviour:
 *  - Watches the sibling country's selected ISO alpha-2 value.
 *  - country === 'AU'              → searchable combobox of au-states.json
 *  - country has ISO 3166-2 subs   → searchable combobox of those subdivisions
 *  - otherwise (or no country yet) → free-text fallback (legacy behaviour)
 *
 * The stored value is always the subdivision NAME (au-states stores the code),
 * matching what ImmiAccount's free-text state field expects.
 */

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TextField } from '../../../components/intake/fields';
import { ComboboxField } from '../../../components/intake/ComboboxField';
import type { FieldDef, RendererProps, RefDataOption } from '../types';
import {
  loadRefData,
  getRefDataSync,
  loadCountrySubdivisions,
  getCountrySubdivisionsSync,
} from '../reference-data/loader';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

const AU_STATES_REF = '/reference-data/au-states.json';

/**
 * Resolve the react-hook-form name of the sibling country field, honouring any
 * repeater namePrefix the way deriveFieldName does (last-segment under prefix).
 */
function deriveSiblingName(
  siblingCanonical: string,
  namePrefix: string | undefined,
): string {
  if (!namePrefix) return siblingCanonical;
  const clean = siblingCanonical.replace(/\[\*\]/g, '');
  const parts = clean.split('.').filter(Boolean);
  return `${namePrefix}.${parts[parts.length - 1]}`;
}

export function SubdivisionR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues, watch } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);
  const hint = resolveHelpText(field, audience);
  const tooltip = lookupFieldTooltip(field.canonical_name);

  const [auStates, setAuStates] = useState<RefDataOption[]>(
    getRefDataSync(AU_STATES_REF) ?? [],
  );
  const [subsLoaded, setSubsLoaded] = useState(false);

  useEffect(() => {
    if (auStates.length === 0) loadRefData(AU_STATES_REF).then(setAuStates);
    loadCountrySubdivisions().then(() => setSubsLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const siblingName = field.subdivisions_of
    ? deriveSiblingName(field.subdivisions_of, namePrefix)
    : undefined;

  if (pdfMode) {
    const value = getValues(name);
    // AU stores the code; map to the friendly label. International is already
    // the name, so render as-is.
    const auLabel = auStates.find((s) => s.value === value)?.label;
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {auLabel ?? value ?? <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  const country = siblingName ? (watch(siblingName) as string | undefined) : undefined;
  const isAustralia = country === 'AU';
  const subdivisions =
    subsLoaded && country && !isAustralia
      ? getCountrySubdivisionsSync(country) ?? []
      : [];

  if (isAustralia) {
    return (
      <ComboboxField
        name={name}
        label={label}
        hint={hint}
        required={field.required}
        options={auStates}
        tooltip={tooltip}
      />
    );
  }

  if (subdivisions.length > 0) {
    return (
      <ComboboxField
        name={name}
        label={label}
        hint={hint}
        required={field.required}
        options={subdivisions}
        tooltip={tooltip}
      />
    );
  }

  // Fallback: no sibling country selected, or that country has no subdivisions.
  return (
    <TextField name={name} label={label} hint={hint} required={field.required} tooltip={tooltip} />
  );
}
